import { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import PrivateRoute from 'components/common/Auth/Private';
import { PermissionsEnum } from 'consts/permissions';
import { ChildRoute } from 'components/common/types/Route.types';

import { PageWrapper } from 'components/common/layout/PageWrapper/PageWrapper';
import { InsightsPaths } from './InsightsConfig';
import InsightsHeader from './InsightsHeader';
import DealHealth from './DealHealth';
import ProofOfPlay from './ProofOfPlay';
import RevenueInsights from './RevenueInsights';
import InventoryReservation from './InventoryReservation/InventoryReservation';
import useHasInsightsAccess from './InsightsAccess';
import POPLiveMonitoring from './POPLiveMonitoring/POPLiveMonitoring';
import ProofOfPlayChina from './ProofOfPlayChina';
import DealHealthChina from './DealHealthChina';
import DealHealthUS from './DealHealthUS';

const Insights: React.FC = () => {
  const { hasAccess } = useHasInsightsAccess();

  const navigateTo = useMemo(() => {
    if (hasAccess.tabs.dealHealthChina) {
      return InsightsPaths.CHINA_DEAL_HEALTH;
    }
    if (hasAccess.tabs.dealHealthUS) {
      return InsightsPaths.US_DEAL_HEALTH;
    }
    return InsightsPaths.DEAL_HEALTH;
  }, [hasAccess.tabs.dealHealthChina, hasAccess.tabs.dealHealthUS]);

  const childRoutes: ChildRoute[] = useMemo(
    () => [
      {
        id: 1,
        path: InsightsPaths.DEAL_HEALTH,
        main: DealHealth,
        pageAccessPermission: undefined,
      },
      {
        id: 2,
        path: InsightsPaths.CHINA_DEAL_HEALTH,
        main: DealHealthChina,
        pageAccessPermission: undefined,
      },
      {
        id: 3,
        path: InsightsPaths.US_DEAL_HEALTH,
        main: DealHealthUS,
        pageAccessPermission: undefined,
      },
      {
        id: 4,
        path: InsightsPaths.PROOF_OF_PLAY,
        main: ProofOfPlay,
        pageAccessPermission: undefined,
      },
      {
        id: 5,
        path: InsightsPaths.CHINA_PROOF_OF_PLAY,
        main: ProofOfPlayChina,
        pageAccessPermission: undefined,
      },
      {
        id: 6,
        path: InsightsPaths.POP_LIVE_MONITORING,
        main: POPLiveMonitoring,
        pageAccessPermission: undefined,
      },
      {
        id: 7,
        path: InsightsPaths.REVENUE_INSIGHTS,
        main: RevenueInsights,
        pageAccessPermission: PermissionsEnum.INSIGHTS_REVENUE_VIEW,
      },
      {
        id: 8,
        path: InsightsPaths.INVENTORY_RESERVATION,
        main: InventoryReservation,
      },
      {
        id: 9,
        path: '*',
        main: () => <Navigate to={navigateTo} replace />,
        pageAccessPermission: undefined,
      },
    ],
    [navigateTo],
  );

  return (
    <PageWrapper>
      <InsightsHeader />
      <Routes>
        {childRoutes.map((route) => (
          <Route
            key={route.id}
            path={route.path}
            element={<PrivateRoute component={route.main} pageAccessPermission={route.pageAccessPermission} />}
          />
        ))}
      </Routes>
    </PageWrapper>
  );
};

export default Insights;
